import RolesGuard from '@/context/auth/RolesGuard';
import { createElement } from 'react';
import SvgColor from '@/commons/svg-color';
import { Roles } from '@/utils/roles';
import { Navigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { translates } from '@/utils/translate';
import { FeaturesType } from '@utils/features';
import FeaturesGuard from '@/context/auth/FeaturesGuard';
import { SidebarItemType } from '@PorkyCol/layouts/dist/cjs/Sidebar/SidebarContent';

const icon = (name: string, custom = false) => {
  if (custom) return <Icon icon={name} width={32} height={32} />;
  return <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />;
};

export const generateRoutes = (routes: Record<string, any>) => {
  const { root, ...rest } = routes;
  const transformRoutes = Object.values(rest).map((route) => {
    const newRoute: Record<string, unknown> = {};
    const newPath = [];
    if (route.path) {
      if (typeof route.path === 'function') {
        newPath.push(route.path(route.params));
      } else {
        newPath.push(route.path);
      }
    }
    newRoute.path = newPath.join('/');
    if (!route.path.length) newRoute.path = '/';
    newRoute.element = createElement(FeaturesGuard, {
      feature: route.feature,
      roles: route.guard,
    });
    newRoute.children = [{ element: createElement(route.element), index: true }];

    return newRoute;
  });
  const newRoute: any = {
    path: root.path,
    children: [
      ...transformRoutes,
      {
        element: <Navigate to={`${root.path}/${Object.values(rest)[0].title}`} replace />,
        index: true,
      },
    ],
  };
  newRoute.element = createElement(FeaturesGuard, {
    feature: root.feature,
    roles: root.guard,
  });
  return newRoute;
};

export const generateRoutesNav = (
  routes: any,
  role = Roles.WAITRESS,
  features: FeaturesType[] = []
): Record<string, any>[] => {
  const { root, ...rest } = routes;
  const newNavigation: Record<string, any> = {
    title: translates[root.label] ?? translates[root.title] ?? root.label ?? root.title,
    key: root.path,
    settings: root.settings,
    active: root.active,
    isFoodCompany: root.isFoodCompany,
    feature: root.feature,
    icon: root.icon,
    hide: root.hide,
    items: [],
  };
  if (features.length && root.feature && role !== Roles.OWNER_SOFT) {
    const feature = features.includes(root.feature);
    if (!feature) return [];
  }

  if (root?.guard) {
    const guards = [...root.guard, Roles.SUPER_ADMIN, Roles.OWNER_SOFT];
    if (!guards.includes(role)) return [];
  }
  if (root?.onlyOption) {
    newNavigation.items = undefined;
    // eslint-disable-next-line consistent-return
    return [newNavigation];
  }

  const separatedRoutes: Record<string, any>[] = [];
  newNavigation.type = SidebarItemType.Nest;
  Object.values(rest).forEach((route: any) => {
    if (!route.show) return;

    if (features.length && route.feature && role !== Roles.OWNER_SOFT) {
      const feature = features.includes(route.feature);
      if (!feature) return;
    }

    if (route?.guard) {
      const guards = [...route.guard, Roles.SUPER_ADMIN, Roles.OWNER_SOFT];
      if (!guards.includes(role)) return;
    }
    const newRoute: Record<string, any> = {
      title: translates[route.label] ?? translates[route.title] ?? route.label ?? route.title,
      key: route.path,
      feature: route.feature,
      isFoodCompany: route.isFoodCompany,
      settings: route.settings,
      active: route.active,
    };
    if (route?.isSeparated) {
      separatedRoutes.push({
        ...newNavigation,
        ...newRoute,
      });
    }
    newNavigation.items.push(newRoute);
  });

  // eslint-disable-next-line consistent-return
  return [newNavigation, ...separatedRoutes];
};

export const resolvePathRoute = (routes: Record<string, any>) => {
  const keys = Object.keys(routes);
  const newRoutes: Record<string, any> = {};
  keys.forEach((key) => {
    const route = routes[key];
    newRoutes[key] = route.path;
  });
  return newRoutes;
};
